import {
    ComputeStatus,
    PurchasedDataSourceExtended,
    StripeSubscriptionStatus,
    ActiveDataSourcesCountConnection,
} from "@biggeo/bg-server-lib/studio";
import { GridColDef } from "@biggeo/bg-ui";
import {
    Box,
    Button,
    CircularLoading,
    ColFilterType,
    DataCloudSourceWrapper,
    DataGridContainer,
    Grid,
    IFilterSearchPaginate,
    MemoizedField,
    Stack,
    Tag,
    Tooltip,
    Typography,
} from "@biggeo/bg-ui/lab";
import {
    AcUnitOutline,
    ArrowSplitOutline,
    CheckCircle,
    Info,
    PublicOutline,
} from "@biggeo/bg-ui/lab/icons";
import startCase from "lodash/startCase";
import { useNavigate } from "react-router";
import { match } from "ts-pattern";

import { Routes } from "../../navigation/redux/model";
import MyDataEmptyView from "./MyDataEmptyView";

interface IPurchasedDataView {
    data: ActiveDataSourcesCountConnection;
    pagination: IFilterSearchPaginate;
    loading: boolean;
}

const tooltips: {
    compute: string;
    status: string;
    exportData: string;
    useDataset: string;
} = {
    compute:
        "Turn on hot compute to enable instant access to your dataset for Datascape use",
    status: "Dataset compute state",
    exportData: "Export data to your cloud storage",
    useDataset: "Launch a new map and unlock your geospatial data's potential",
};

const PurchasedDataView = ({
    data,
    pagination,
    loading,
}: IPurchasedDataView) => {
    const toPage = useNavigate();
    const columns: GridColDef<PurchasedDataSourceExtended>[] = [
        {
            field: "listing",
            headerName: "Listing",
            width: 100,
            sortable: false,
            filterable: false,
            headerAlign: "center",
            align: "center",
            renderCell: ({ row }) => {
                return (
                    <MemoizedField>
                        <DataCloudSourceWrapper
                            cloudInfo={
                                row.etlDatasetLocation?.cloudStorageInfo ||
                                undefined
                            }
                            fileType={row.etlDatasetLocation?.fileType}
                            cronInterval={
                                row.etlDatasetLocation?.cronInterval ||
                                undefined
                            }
                        />
                    </MemoizedField>
                );
            },
        },
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            minWidth: 300,
            sortable: false,
            type: ColFilterType.string,
            filterable: true,
            headerAlign: "left",
            align: "center",
            renderCell: ({ row }) => {
                return (
                    <MemoizedField
                        title={
                            row.dataSourcePermission.dataSourcePriceName ||
                            row.dataSource.name
                        }
                        subTitle={
                            row.dataSourcePermission
                                .dataSourcePriceDescription || undefined
                        }
                    />
                );
            },
        },
        {
            field: "exportData",
            headerName: "Export Data",
            flex: 1,
            minWidth: 160,
            filterable: false,
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderHeader: (params) => (
                <Stack flexDirection="row" alignItems="center" gap={1}>
                    <Typography variant="body3" fontWeight="semibold">
                        {startCase(params.field)}
                    </Typography>
                    <Tooltip title={tooltips.exportData} sx={{ maxWidth: 52 }}>
                        <Box>
                            <Info color="primary" size="xxs" />
                        </Box>
                    </Tooltip>
                </Stack>
            ),
            renderCell: ({ row }) => {
                const canExport =
                    row.dataSourcePermission.dataSourcePriceEgress;

                return (
                    <MemoizedField
                        disable={
                            !canExport ||
                            row.dataSourcePermission
                                .stripeSubscriptionStatus ===
                                StripeSubscriptionStatus.incomplete_expired
                        }
                    >
                        <Button
                            variant="outlined"
                            endNode={<ArrowSplitOutline size="xs" />}
                            disabled={!canExport}
                            onClick={() => {
                                toPage(
                                    `${Routes.purchasedData}/egress/${row.dataSourcePermission.id}`
                                );
                            }}
                        >
                            Egress Data
                        </Button>
                    </MemoizedField>
                );
            },
        },
        {
            field: "useDataset",
            headerName: "Use Dataset",
            flex: 1,
            minWidth: 160,
            filterable: false,
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderCell: ({ row }) => {
                const hasDatascapeAccess =
                    row.dataSourcePermission.dataSourcePriceDatascape;

                return (
                    <MemoizedField disable={!hasDatascapeAccess}>
                        <Button
                            variant="outlined"
                            endNode={<PublicOutline size="xs" />}
                            onClick={() => {
                                toPage(
                                    `${Routes.mapTemplatesCreate}/${row.dataSourcePermission.fkDataSourceId}`
                                );
                            }}
                        >
                            Create Map
                        </Button>
                    </MemoizedField>
                );
            },
        },
        {
            field: "computeStatus",
            headerName: "Compute",
            flex: 1,
            minWidth: 180,
            filterable: true,
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderHeader: () => (
                <Stack flexDirection="row" alignItems="center" gap={1}>
                    <Typography variant="body3" fontWeight="semibold">
                        {startCase("Resources")}
                    </Typography>
                    <Tooltip title={tooltips.compute} sx={{ maxWidth: 52 }}>
                        <Box>
                            <Info color="primary" size="xxs" />
                        </Box>
                    </Tooltip>
                </Stack>
            ),
            renderCell: ({ row }) => {
                const status = row.configuration?.status;
                return (
                    <MemoizedField>
                        <Grid
                            container
                            spacing={1}
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                padding: 1,
                            }}
                        >
                            <Grid item>
                                <Typography
                                    variant="body3"
                                    fontWeight="semibold"
                                >
                                    {match(status)
                                        .with(ComputeStatus.cold, () => "Cold")
                                        .with(ComputeStatus.hot, () => "Hot")
                                        .with(
                                            ComputeStatus.loading,
                                            () => "Loading"
                                        )
                                        .otherwise(() => "Cold")}
                                </Typography>
                            </Grid>
                            <Grid item>
                                {match(status)
                                    .with(ComputeStatus.cold, () => (
                                        <AcUnitOutline
                                            color="info"
                                            size="xxs"
                                        />
                                    ))
                                    .with(ComputeStatus.hot, () => (
                                        <CheckCircle
                                            color="success"
                                            size="xxs"
                                        />
                                    ))
                                    .with(ComputeStatus.loading, () => (
                                        <CircularLoading size="xxs" />
                                    ))
                                    .otherwise(() => (
                                        <></>
                                    ))}
                            </Grid>
                        </Grid>
                    </MemoizedField>
                );
            },
        },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            minWidth: 180,
            filterable: true,
            type: ColFilterType.string,
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderCell: ({ row }) => {
                const status =
                    row.dataSourcePermission.stripeSubscriptionStatus;

                if (!status) return <MemoizedField title="-" />;

                return (
                    <MemoizedField>
                        <Tag
                            rounded
                            density="dense"
                            variant="tonal"
                            color={
                                status === StripeSubscriptionStatus.active
                                    ? "success"
                                    : status ===
                                        StripeSubscriptionStatus.canceled
                                      ? "error"
                                      : "warning"
                            }
                        >
                            {startCase(status)}
                        </Tag>
                    </MemoizedField>
                );
            },
        },
        // {
        //     field: "price",
        //     headerName: "Price",
        //     minWidth: 140,
        //     sortable: false,
        //     type: ColFilterType.string,
        //     headerAlign: "left",
        //     renderCell: ({ row }) => {
        //         const price = row.purchasedDataset?.pricing?.[0];
        //         return (
        //             <Stack flexDirection="row" alignItems="center" gap={2}>
        //                 <Typography variant="body3" fontWeight="semibold">
        //                     {`US$${formatNumberWithCommas(price?.price || 0)}`}
        //                 </Typography>
        //             </Stack>
        //         );
        //     },
        // },
        // {
        //     field: "pricingPlan",
        //     headerName: "Pricing Plan",
        //     minWidth: 140,
        //     sortable: false,
        //     type: ColFilterType.string,
        //     headerAlign: "left",
        //     renderCell: ({ row }) => {
        //         const price = row.dataset?.pricing?.[0];
        //         return (
        //             <Stack flexDirection="row" alignItems="center" gap={2}>
        //                 <Typography variant="body3" fontWeight="semibold">
        //                     {price?.priceOption === PriceOption.Subscription
        //                         ? price.frequency
        //                         : price?.priceOption ===
        //                             PriceOption.OneTimePurchase
        //                           ? "One-time"
        //                           : price?.priceOption}
        //                 </Typography>
        //             </Stack>
        //         );
        //     },
        // },
        // {
        //     field: "startDate",
        //     headerName: "Start Date",
        //     minWidth: 180,
        //     sortable: false,
        //     type: ColFilterType.date,
        //     headerAlign: "left",
        //     renderCell: ({ row }) => {
        //         return (
        //             <Stack flexDirection="row" alignItems="center" gap={2}>
        //                 <Typography variant="body3" fontWeight="semibold">
        //                     {formatDateMMMDYYYY(
        //                         row.purchasedDataset.subscriptionStartedAt ||
        //                             row.purchasedDataset.createdAt
        //                     )}
        //                 </Typography>
        //             </Stack>
        //         );
        //     },
        // },
        // {
        //     field: "renewalDate",
        //     headerName: "Renewal Date",
        //     minWidth: 140,
        //     sortable: false,
        //     type: ColFilterType.date,
        //     headerAlign: "left",
        //     renderCell: ({ row }) => {
        //         return (
        //             <Stack flexDirection="row" alignItems="center" gap={2}>
        //                 <Typography variant="body3" fontWeight="semibold">
        //                     {row.purchasedDataset.subscriptionCurrentPeriodEnd
        //                         ? formatDateMMMDYYYY(
        //                               row.purchasedDataset
        //                                   .subscriptionCurrentPeriodEnd
        //                           )
        //                         : "—"}
        //                 </Typography>
        //             </Stack>
        //         );
        //     },
        // },
        // {
        //     field: "manage",
        //     minWidth: 124,
        //     filterable: true,
        //     sortable: false,
        //     headerName: "",
        //     renderCell: () => {
        //         return (
        //             <MemoizedField>
        //                 <Button
        //                     fullWidth
        //                     variant="outlined"
        //                     onClick={() => {
        //                         //
        //                     }}
        //                 >
        //                     Manage
        //                 </Button>
        //             </MemoizedField>
        //         );
        //     },
        // },
    ];

    return (
        <Stack
            height="100%"
            width="100%"
            sx={{
                paddingTop: 4,
            }}
        >
            <DataGridContainer
                loading={loading}
                pinnedColumns={{ right: ["manage"] }}
                columns={columns}
                rows={data.activeDataSources}
                getRowId={(r) => r.dataSource.id}
                rowCount={data.count}
                initialState={{
                    pinnedColumns: {
                        right: ["manage"],
                    },
                }}
                pagination
                hideFilterChips
                filterSearchPaginateProps={pagination}
                enableColumnResize
                customNoRowsOverlay={<MyDataEmptyView />}
            />
        </Stack>
    );
};

export default PurchasedDataView;
